import type { FC } from 'react'

import { LoaderCircle, Popup, Table } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LocalDateTime } from '@/components/LocalDateTime'

import adminQueries from '../../queries'
import type { EmployeeConsentDetails } from '../../resources'
import adminResources from '../../resources'

type HistoryRow = {
  id: number
  type: string
  date: string
}

type CostObjectHistoryProps = {
  consent: Omit<EmployeeConsentDetails, 'eligibleCostObjects'>
  costObject?: EmployeeConsentDetails['eligibleCostObjects'][number]
  onClose: () => void
}

export const CostObjectHistory: FC<CostObjectHistoryProps> = ({ consent, costObject, onClose }) => {
  const translate = useTranslation()

  if (!costObject?.id) return null

  const { data, isLoading } = useQuery(
    adminQueries.adminConsentReports(consent.id, costObject.id),
    () => adminResources.getConsentHistoryPerCostObject(consent.id, costObject.id),
    { enabled: Boolean(consent.id && costObject.id) }
  )

  const historyRows: HistoryRow[] = data?.length
    ? data.map(({ id, type, createdAt }) => ({
        id,
        type,
        date: createdAt,
      }))
    : [
        ...(costObject.approvedDate
          ? [
              {
                id: costObject.id,
                type: 'accepted',
                date: costObject.approvedDate,
              },
            ]
          : []),
        ...(costObject.revokedDate
          ? [
              {
                id: costObject.id,
                type: 'revoked',
                date: costObject.revokedDate,
              },
            ]
          : []),
      ]

  return (
    <Popup variant="medium" ariaLabel={translate('Cost object history')} isOpen onClose={onClose}>
      <Table>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={2} className="text-center">
                <LoaderCircle />
              </td>
            </tr>
          ) : (
            historyRows.map(({ id, type, date }) => (
              <tr key={id}>
                <td>
                  <Translation id={`consent-history-action/${type}`} />
                </td>
                <td>
                  <LocalDateTime date={date} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Popup>
  )
}
